import { useCallback, useEffect, useRef, useState } from 'react';
import { connectEvents, deleteDriver, fetchDrivers, upsertDriver } from './api';
import type { Driver, DriverPatch, Environment, ServerEvent } from './types';

export type Toast = { id: number, message: string, undo?: () => void, error?: boolean };

type Drivers = Map<string, Driver>;

const isNewer = (candidate: string | undefined, than: string | undefined) => (candidate || '') >= (than || '');

// The shared list of drivers: loaded once, then kept current by websocket events.
// Local changes show immediately and are rolled back if the server refuses them.
export function useDrivers(env: Environment) {
    const [drivers, setDrivers] = useState<Drivers>(new Map());
    const [loaded, setLoaded] = useState(false);
    const [live, setLive] = useState(false);
    const [users, setUsers] = useState<string[]>([]);
    const [toasts, setToasts] = useState<Toast[]>([]);
    const driversRef = useRef(drivers);
    driversRef.current = drivers;
    const nextToastId = useRef(1);

    const dismissToast = useCallback((id: number) => setToasts((current) => current.filter((toast) => toast.id !== id)), []);
    const showToast = useCallback((toast: Omit<Toast, 'id'>) => {
        const id = nextToastId.current++;
        setToasts((current) => [...current.slice(-2), { ...toast, id }]);
        window.setTimeout(() => dismissToast(id), toast.undo ? 12000 : 5000);
    }, [dismissToast]);

    const reload = useCallback(async () => {
        try {
            const list = await fetchDrivers(env);
            setDrivers(new Map(list.map((driver) => [driver.truckNumber, driver])));
            setLoaded(true);
        } catch (error) {
            showToast({ message: `Could not load drivers: ${(error as Error).message}`, error: true });
        }
    }, [env, showToast]);

    useEffect(() => {
        const applyEvent = (event: ServerEvent) => {
            if (event.type === 'presence') {
                setUsers(event.users);
                return;
            }
            setDrivers((current) => {
                const next = new Map(current);
                if (event.type === 'upsert') {
                    if (isNewer(event.driver.lastChanged, current.get(event.driver.truckNumber)?.lastChanged)) {
                        next.set(event.driver.truckNumber, event.driver);
                    }
                } else if (isNewer(event.lastChanged, current.get(event.truckNumber)?.lastChanged)) {
                    next.delete(event.truckNumber);
                }
                return next;
            });
        };
        return connectEvents(env, {
            onEvent: applyEvent,
            onOpen: () => { setLive(true); reload(); },
            onClose: () => setLive(false),
        });
    }, [env, reload]);

    const replace = (remove: string | undefined, add: Driver | undefined) => setDrivers((current) => {
        const next = new Map(current);
        if (remove !== undefined) {
            next.delete(remove);
        }
        if (add) {
            next.set(add.truckNumber, add);
        }
        return next;
    });

    // Creates or changes a driver. Resolves to false if the server refused.
    const save = useCallback(async (truckNumber: string, patch: DriverPatch, newTruckNumber?: string): Promise<boolean> => {
        const before = driversRef.current.get(truckNumber);
        const target = newTruckNumber?.trim() || truckNumber;
        if (before || patch.position) {
            replace(truckNumber, { ...before, ...patch, truckNumber: target } as Driver);
        }
        try {
            const driver = await upsertDriver(env, { truckNumber, patch, newTruckNumber: target === truckNumber ? undefined : target });
            replace(undefined, driver);
            return true;
        } catch (error) {
            replace(target, before);
            showToast({ message: (error as Error).message, error: true });
            return false;
        }
    }, [env, showToast]);

    const move = useCallback(async (truckNumber: string, patch: DriverPatch, description: string) => {
        const before = driversRef.current.get(truckNumber);
        if (await save(truckNumber, patch) && before) {
            const undo = () => save(truckNumber, { position: before.position, parked: !!before.parked });
            showToast({ message: description, undo });
        }
    }, [save, showToast]);

    const remove = useCallback(async (truckNumber: string) => {
        const before = driversRef.current.get(truckNumber);
        if (!before) {
            return;
        }
        replace(truckNumber, undefined);
        try {
            await deleteDriver(env, { truckNumber });
            const { truckNumber: _truckNumber, lastChanged: _lastChanged, updatedBy: _updatedBy, ...fields } = before;
            showToast({ message: `Deleted ${truckNumber}`, undo: () => save(truckNumber, fields) });
        } catch (error) {
            replace(undefined, before);
            showToast({ message: (error as Error).message, error: true });
        }
    }, [env, save, showToast]);

    return { drivers, loaded, live, users, toasts, dismissToast, save, move, remove };
}
